import { Analysis, Granularity, Metric } from './dashboard-config.model';
import { GRANULARITY_PROFILES } from './granularities/granularity-profile';

/**
 * Facts that hold only for a combination of axes. Everything true for one axis alone lives on
 * that axis's profile (metric, granularity, analysis).
 */

/** Dataset granularity segments that differ from the granularity's own token for one analysis. */
export const DATASET_TOKEN_OVERRIDES: readonly { analysis: Analysis; granularity: Granularity; token: string }[] = [
  // Ranking datasets use `s` and `sm_d` where every other analysis uses `sm` and `d`.
  { analysis: Analysis.Ranking, granularity: Granularity.States, token: 's' },
  { analysis: Analysis.Ranking, granularity: Granularity.DistrictSubMarkets, token: 'sm_d' },
  // Dossier datasets have no granularity segment (`ds__{metric}__dssr__vm`).
  { analysis: Analysis.Dossier, granularity: Granularity.States, token: '' },
];

function isDistrictLevel(granularity: Granularity): boolean {
  return GRANULARITY_PROFILES[granularity]?.traits.includes('district-level') ?? false;
}

/* ==========================================================================
 * District-level defaults
 * ========================================================================== */

export interface YearWindow {
  minYear: number;
  maxYear: number;
  /** First year initially selected, for year ranges. */
  defaultMinYear?: number;
}

const COMPANIES_AND_ECONOMIC_PARTICIPANTS: readonly Metric[] = [
  Metric.CompaniesCount,
  Metric.CompaniesRevenue,
  Metric.EconomicParticipant,
];

/** GVA has the same Kreise datasets as companies, except index (pending dataset). */
const COMPANIES_ECONOMIC_PARTICIPANTS_AND_GVA: readonly Metric[] = [
  ...COMPANIES_AND_ECONOMIC_PARTICIPANTS,
  Metric.GrossValueAdded,
];

/**
 * Fixed year windows at district-level granularities; elsewhere the years come from the dataset.
 * Metrics not listed (Employed, Self-employed, Tourism, GVA in index) keep their usual time filter.
 */
export const DISTRICT_YEAR_WINDOWS: readonly {
  analysis: Analysis;
  metrics: readonly Metric[];
  /** District-level granularities the window applies to. Default: all of them. */
  granularities?: readonly Granularity[];
  window: YearWindow;
}[] = [
  {
    analysis: Analysis.TimeSeries,
    metrics: COMPANIES_ECONOMIC_PARTICIPANTS_AND_GVA,
    window: { minYear: 2018, maxYear: 2023, defaultMinYear: 2021 },
  },
  { analysis: Analysis.Ranking, metrics: COMPANIES_ECONOMIC_PARTICIPANTS_AND_GVA, window: { minYear: 2018, maxYear: 2023 } },
  // Comparison structure only; the four-grid at Bezirke × Teilmärkte uses the automatic years.
  {
    analysis: Analysis.Comparison,
    metrics: COMPANIES_ECONOMIC_PARTICIPANTS_AND_GVA,
    granularities: [Granularity.Districts],
    window: { minYear: 2018, maxYear: 2023 },
  },
  // Index ranking only; index development (Bezirke × Teilmärkte) has its own slider and range filters.
  {
    analysis: Analysis.Index,
    metrics: COMPANIES_AND_ECONOMIC_PARTICIPANTS,
    granularities: [Granularity.Districts],
    window: { minYear: 2009, maxYear: 2023 },
  },
];

export function districtYearWindow(analysis: Analysis, metric: Metric, granularity: Granularity): YearWindow | undefined {
  if (!isDistrictLevel(granularity)) return undefined;
  return DISTRICT_YEAR_WINDOWS.find(
    (rule) =>
      rule.analysis === analysis &&
      rule.metrics.includes(metric) &&
      (!rule.granularities || rule.granularities.includes(granularity)),
  )?.window;
}

/** Ids preselected at district-level granularities; single selects take the first. */
export interface DistrictSelections {
  states?: readonly string[];
  districts?: readonly string[];
}

export const DISTRICT_SELECTIONS: readonly { metrics: readonly Metric[]; selections: DistrictSelections }[] = [
  {
    metrics: COMPANIES_ECONOMIC_PARTICIPANTS_AND_GVA,
    selections: { states: ['1', '2'], districts: ['185', '203', '245', '290'] },
  },
  { metrics: [Metric.Employed], selections: { districts: ['331', '245', '85', '124'] } },
];

export function districtSelections(metric: Metric, granularity: Granularity): DistrictSelections | undefined {
  if (!isDistrictLevel(granularity)) return undefined;
  return DISTRICT_SELECTIONS.find((rule) => rule.metrics.includes(metric))?.selections;
}

/* ==========================================================================
 * Catalog invariants
 * ========================================================================== */

/**
 * Permanent facts the catalogs must respect, checked by the registry spec; they never create
 * dashboards. Gaps from missing datasets are not invariants: they stay as commented omissions in
 * the catalogs until the data arrives.
 */
export interface CatalogInvariant {
  metric: Metric;
  onlyGranularities: readonly Granularity[];
}

export const CATALOG_INVARIANTS: readonly CatalogInvariant[] = [
  // Tourism has its own granularity (no state, district or market breakdown in the dashboard id).
  { metric: Metric.Tourism, onlyGranularities: [Granularity.Unknown] },
];
